import { useEffect, useState } from "react";
import { Alert, Button, Input, InputNumber, Select, Switch, Table, Tabs, Tag, Tooltip, Upload, message } from "antd";
import { DeleteOutlined, DownloadOutlined, ReloadOutlined, SaveOutlined, UploadOutlined } from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../core/service/api-service";
import "./config.css";

/**
 * appsettings.json yönetim paneli.
 *
 * Form ŞEMADAN üretilir (backend'deki ConfigDoc nitelikleri): yeni bir ayar eklendiğinde burada
 * kod değiştirmeye gerek yoktur, alan kendiliğinden görünür. Böylece "ayar eklendi ama arayüze
 * konmadı, kimse bilmiyor" durumu doğmaz.
 *
 * Notlar:
 *  - DB bağlantıları bu panelde YOKTUR: backend yalnız RootObject bölümlerini yazar, ConnectionStrings
 *    dosyada olduğu gibi korunur.
 *  - Gizli alanların değeri sunucudan hiç gelmez ("***"). Dokunulmazsa eski değer korunur.
 *  - Bazı ayarlar açılışta sabitlenir (RequiresRestart) → kaydetmek yetmez, uyarı gösterilir.
 */
export default function ConfigPanel()
{
    const [sections, setSections] = useState([]);
    const [changes, setChanges] = useState({});     // { "App.JwtKey": value }
    const [saving, setSaving] = useState(false);
    const [restartNeeded, setRestartNeeded] = useState([]);

    const load = async () => {
        const res = await ApiService.Config.GetSchema();
        if (res?.success) {
            setSections(res.data || []);
            setChanges({});
        }
    };

    useEffect(() => {
        load();
    }, []);

    const dirtyCount = Object.keys(changes).length;

    const setValue = (key, value) => setChanges((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        const updates = Object.entries(changes).map(([key, value]) => ({ key, value }));
        if (updates.length === 0) return;

        setSaving(true);
        const res = await ApiService.Config.SaveSettings(updates);
        setSaving(false);

        if (res?.success) {
            setRestartNeeded(res.data?.requiresRestart ? (res.data?.restartKeys || []) : []);
            await load();
        }
    };

    return (
        <div className="config-panel">
            <div className="config-toolbar">
                <div className="config-intro">
                    {t("Settings live in appsettings.json on the server. Saving here rewrites that file and reloads the configuration — database connections are not editable.")}
                </div>
                <div className="config-actions">
                    <Button icon={<ReloadOutlined />} onClick={load}>{t("Reload")}</Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        disabled={dirtyCount === 0}
                        onClick={save}
                    >
                        {t("Save")}{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
                    </Button>
                </div>
            </div>

            {restartNeeded.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    className="config-restart"
                    message={t("Saved. Some settings only take effect after a restart:")}
                    description={restartNeeded.join(", ")}
                    closable
                    onClose={() => setRestartNeeded([])}
                />
            )}

            <Tabs
                size="small"
                className="config-tabs"
                tabPosition="top"
                items={sections.map((section) => ({
                    key: section.key,
                    // Sekme içeriği gizli olduğu için, başka bölümdeki kaydedilmemiş değişiklik
                    // görünmez kalırdı; sayıyı sekme etiketinde göster.
                    label: (
                        <span className="config-tab-label">
                            {section.name}
                            {dirtyInSection(changes, section.key) > 0 && (
                                <Tag className="config-tag-dirty">{dirtyInSection(changes, section.key)}</Tag>
                            )}
                        </span>
                    ),
                    children: (
                        <SectionFields
                            nodes={section.children || []}
                            changes={changes}
                            onChange={setValue}
                        />
                    ),
                }))}
            />
        </div>
    );
}

/** Bir bölümdeki kaydedilmemiş alan sayısı. Anahtarlar "App.JwtKey" biçiminde. */
function dirtyInSection(changes, sectionKey)
{
    return Object.keys(changes).filter((key) => key === sectionKey || key.startsWith(`${sectionKey}.`)).length;
}

function SectionFields({ nodes, changes, onChange })
{
    return (
        <div className="config-fields">
            {nodes.map((node) =>
                node.children?.length
                    ? (
                        <div className="config-group" key={node.key}>
                            <div className="config-group-title">{node.name}</div>
                            <SectionFields nodes={node.children} changes={changes} onChange={onChange} />
                        </div>
                    )
                    : <ConfigField key={node.key} node={node} changes={changes} onChange={onChange} />
            )}
        </div>
    );
}

function ConfigField({ node, changes, onChange })
{
    const dirty = Object.prototype.hasOwnProperty.call(changes, node.key);
    const value = dirty ? changes[node.key] : node.value;

    // Ad + açıklama tek satırda: 4K ekranda onlarca ayar tek ekrana sığsın diye açıklama tooltip'te.
    const label = (
        <Tooltip title={node.description || null} mouseEnterDelay={0.4}>
            <span className="config-field-name">
                {node.name}
                {node.requiresRestart && <Tag className="config-tag-restart">{t("restart")}</Tag>}
                {node.secret && <Tag className="config-tag-secret">{t("secret")}</Tag>}
            </span>
        </Tooltip>
    );

    return (
        <div className={`config-field ${dirty ? "is-dirty" : ""}`}>
            <div className="config-field-label">
                {label}
                <code className="config-field-key">{node.key}</code>
            </div>

            <div className="config-field-control">
                <Control node={node} value={value} onChange={(v) => onChange(node.key, v)} />
                {(node.kind === "file" || node.kind === "directory") && <FileControl node={node} />}
            </div>
        </div>
    );
}

function Control({ node, value, onChange })
{
    if (node.type === "bool") {
        return <Switch size="small" checked={value === true || value === "true"} onChange={onChange} />;
    }

    if (node.allowedValues?.length) {
        return (
            <Select
                size="small"
                value={value ?? undefined}
                onChange={onChange}
                options={node.allowedValues.map((v) => ({ value: v, label: String(v) }))}
                className="config-input"
                allowClear
            />
        );
    }

    if (node.type === "int" || node.type === "number") {
        return <InputNumber size="small" value={value} onChange={onChange} className="config-input-num" />;
    }

    if (node.secret) {
        // Değer sunucudan hiç gelmez; dokunulmazsa eski sır korunur.
        return (
            <Input.Password
                size="small"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t("Leave as *** to keep the current value")}
                className="config-input"
            />
        );
    }

    if (node.type === "list" || node.type === "map") {
        // Liste/sözlük alanları JSON olarak düzenlenir (ör. CORS origin listesi, Storage.Map).
        return (
            <Input.TextArea
                autoSize={{ minRows: 1, maxRows: 6 }}
                value={typeof value === "string" ? value : JSON.stringify(value ?? (node.type === "list" ? [] : {}), null, 2)}
                onChange={(e) => onChange(e.target.value)}
                className="config-input config-input-json"
            />
        );
    }

    return (
        <Input
            size="small"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="config-input"
        />
    );
}

/** Dosya tipli ayar: içeriği yükle/indir. Klasör tipli ayar: içine dosya at, listele, sil. */
function FileControl({ node })
{
    const isDirectory = node.kind === "directory";
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);   // klasör içeriği istenmedikçe kapalı — yer kaplamasın

    const loadFiles = async () => {
        if (!isDirectory || !node.value) return;
        setLoading(true);
        const res = await ApiService.Config.ListFiles(node.key);
        setLoading(false);
        if (res?.success) setFiles(res.data || []);
    };

    useEffect(() => {
        if (open) loadFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.key, node.value, open]);

    const upload = async (file) => {
        const res = await ApiService.Config.UploadFile(node.key, file);
        if (res?.success) {
            message.success(t("Uploaded"));
            await loadFiles();
        }
        return false;   // antd'nin kendi isteğini engelle
    };

    const remove = async (name) => {
        const res = await ApiService.Config.DeleteFile(node.key, name);
        if (res?.success) await loadFiles();
    };

    return (
        <div className="config-file">
            <div className="config-file-actions">
                <Upload showUploadList={false} beforeUpload={upload}>
                    <Tooltip title={isDirectory ? t("Upload into folder") : t("Upload file")}>
                        <Button size="small" type="text" icon={<UploadOutlined />} disabled={!node.value} />
                    </Tooltip>
                </Upload>

                {!isDirectory && (
                    <Tooltip title={t("Download")}>
                        <Button
                            size="small"
                            type="text"
                            icon={<DownloadOutlined />}
                            disabled={!node.value}
                            href={ApiService.Config.DownloadFileUrl(node.key)}
                            target="_blank"
                        />
                    </Tooltip>
                )}

                {isDirectory && node.value && (
                    <Button size="small" type="link" className="config-file-toggle" onClick={() => setOpen((v) => !v)}>
                        {open ? t("Hide files") : t("Files")}
                    </Button>
                )}

                {!node.value && (
                    <span className="config-hint">{t("Set the path and save first.")}</span>
                )}
            </div>

            {isDirectory && node.value && open && (
                <Table
                    size="small"
                    rowKey="name"
                    loading={loading}
                    dataSource={files}
                    pagination={files.length > 10 ? { pageSize: 10 } : false}
                    className="config-file-table"
                    columns={[
                        { title: t("File"), dataIndex: "name" },
                        {
                            title: t("Size"),
                            dataIndex: "size",
                            width: 110,
                            render: (size) => `${(size / 1024).toFixed(0)} KB`,
                        },
                        {
                            title: "",
                            width: 90,
                            render: (_, row) => (
                                <span className="config-file-row-actions">
                                    <a href={ApiService.Config.DownloadFileUrl(node.key, row.name)} target="_blank" rel="noreferrer">
                                        <DownloadOutlined />
                                    </a>
                                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(row.name)} />
                                </span>
                            ),
                        },
                    ]}
                />
            )}
        </div>
    );
}
